import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const DialogContext = createContext(null);

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const triggerRef = useRef(null);

  const open = useCallback((type, message, options = {}) => new Promise((resolve) => {
    triggerRef.current = document.activeElement;
    setDialog({ type, message, title: options.title, confirmLabel: options.confirmLabel, resolve });
  }), []);
  const confirm = useCallback((message, options = {}) => open('confirm', message, options), [open]);
  const notify = useCallback((message, options = {}) => open('notice', message, options), [open]);
  const close = useCallback((result) => {
    if (!dialog) return;
    dialog.resolve(result);
    setDialog(null);
    requestAnimationFrame(() => triggerRef.current?.focus?.());
  }, [dialog]);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, dialog]);

  return (
    <DialogContext.Provider value={{ confirm, notify }}>
      {children}
      {dialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) close(false); }}>
          <section role="dialog" aria-modal="true" aria-labelledby="app-dialog-title" className="w-full max-w-md rounded-xl3 border border-line bg-surface p-6 shadow-glass">
            <h2 id="app-dialog-title" className="text-xl font-semibold text-ink">{dialog.title || (dialog.type === 'confirm' ? 'Подтвердите действие' : 'Сообщение')}</h2>
            <p className="mt-3 text-sm leading-6 text-ink-subtle">{dialog.message}</p>
            <div className="mt-6 flex justify-end gap-3">
              {dialog.type === 'confirm' && <button type="button" onClick={() => close(false)} className="rounded-xl border border-line bg-surface px-4 py-2.5 text-sm font-medium text-ink">Отмена</button>}
              <button type="button" autoFocus onClick={() => close(true)} className={`rounded-xl px-4 py-2.5 text-sm font-medium ${dialog.type === 'confirm' ? 'bg-red-500 text-white' : 'bg-accent text-accent-ink'}`}>{dialog.confirmLabel || (dialog.type === 'confirm' ? 'Удалить' : 'Понятно')}</button>
            </div>
          </section>
        </div>
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialog must be used inside DialogProvider');
  return context;
};
